// Builds a study plan from the planner inputs. Pure, so the builder can rerun it on every change.

export const DAY_MS = 86400000;
export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// Placeholder until the plan API supplies the app's question pool
export const DEFAULT_POOL_SIZE = 3200;
export const SECONDS_PER_QUESTION = 90;
export const MIN_QUESTIONS_PER_DAY = 10;
export const LESSON_SIZE = 10;
export const POP_MINIMUM = 100;
export const POP_INTERVAL = 50;
export const POP_TARGET = 3000;
export const MIN_DAYS_OUT = 7;
export const MAX_DAYS_OUT = 548;
export const NO_DATE_DAYS_OUT = 56;
// How often a study day is held back for catch-up
export const CATCH_UP_OPTIONS = [
  { key: 'none', label: 'None' },
  { key: 'biweekly', label: 'Every other week' },
  { key: 'weekly', label: 'Every week' },
];

export function startOfDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

// Monday-first weekday index (0 = Mon)
export function weekdayIndex(date) {
  return (date.getDay() + 6) % 7;
}

export function weekKey(date) {
  const monday = new Date(date);
  monday.setDate(monday.getDate() - weekdayIndex(monday));
  return monday.toDateString();
}

export function addDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function daysBetween(from, to) {
  return Math.round((startOfDay(to) - startOfDay(from)) / DAY_MS);
}

export function questionsForMinutes(minutes) {
  return Math.round(minutes * 60 / SECONDS_PER_QUESTION);
}

/**
 * @param {Object} inputs
 * @param {Date} inputs.today
 * @param {number} inputs.daysOut - days from today to the exam
 * @param {boolean[]} inputs.studyDays - Monday-first, one per weekday
 * @param {number} inputs.minutes - minutes on a study day
 * @param {string} [inputs.catchUp] - a CATCH_UP_OPTIONS key
 * @param {number} [inputs.poolSize]
 * @param {number} [inputs.answeredSoFar] - questions already counted toward Possibility of Passing
 * @param {number} [inputs.answeredToday] - questions answered so far today
 */
export function buildPlan({
  today, daysOut, studyDays, minutes, catchUp = 'none', poolSize = DEFAULT_POOL_SIZE, answeredSoFar = 0, answeredToday = 0,
}) {
  const start = startOfDay(today);
  const exam = addDays(start, daysOut);
  const days = [];

  for (let i = 1; i <= daysOut; i++) {
    const date = addDays(start, i);
    const wd = weekdayIndex(date);
    days.push({ date, wd, kind: i === daysOut ? 'exam' : (studyDays[wd] ? 'study' : 'off') });
  }

  // The day before the exam is rest
  if (days.length > 1 && days[days.length - 2].kind === 'study') {
    days[days.length - 2].kind = 'off';
  }

  // Hold the last study day of the week for catch-up, weekly or every other week
  if (catchUp !== 'none') {
    const byWeek = {};
    days.forEach((day, i) => {
      if (day.kind === 'study') (byWeek[weekKey(day.date)] ||= []).push(i);
    });
    Object.values(byWeek).forEach((indexes, week) => {
      if (catchUp === 'biweekly' && week % 2 === 1) return;
      if (indexes.length >= 2) days[indexes[indexes.length - 1]].kind = 'reserve';
    });
  }

  const studyIndexes = days.map((day, i) => (day.kind === 'study' ? i : -1)).filter(i => i >= 0);
  const studyCount = studyIndexes.length;
  studyIndexes.forEach((i, k) => {
    const fraction = k / Math.max(studyCount, 1);
    days[i].phase = fraction < 0.5 ? 'learn' : fraction < 0.8 ? 'practice' : 'simulate';
  });

  const cap = Math.max(MIN_QUESTIONS_PER_DAY, questionsForMinutes(minutes));
  const target = Math.ceil(poolSize / Math.max(studyCount, 1));
  const questionsPerDay = Math.max(MIN_QUESTIONS_PER_DAY, Math.min(target, cap));

  // Checkpoints
  const checkpoints = [];
  const mark = (i, label, type) => {
    if (i == null || i < 0) return;
    days[i].checkpoint = true;
    checkpoints.push({ date: days[i].date, label, type });
  };
  let lastWeek = days.length ? weekKey(days[0].date) : null;
  studyIndexes.forEach(i => {
    const week = weekKey(days[i].date);
    if (week !== lastWeek && days[i].phase !== 'simulate') mark(i, 'Readiness check, 25 questions', 'readiness');
    lastWeek = week;
  });
  mark(studyIndexes.find(i => days[i].phase === 'practice'), 'Adaptive CAT sim, see where you stand', 'cat');
  mark(studyIndexes.find(i => days[i].phase === 'simulate'), 'Adaptive CAT sim', 'cat');
  mark(studyIndexes[studyIndexes.length - 3] ?? studyIndexes[studyIndexes.length - 1], 'Full-length exam simulation', 'simulation');

  // Daily mix: lessons lead early on, practice questions take over; both count toward Possibility of Passing
  const lessonsFor = phase => {
    if (phase === 'learn') return Math.max(1, Math.round(questionsPerDay * 0.5 / LESSON_SIZE));
    return phase === 'practice' ? 1 : 0;
  };
  // Start from what's already answered plus the rest of today's goal
  const todayTarget = studyDays[weekdayIndex(start)] ? questionsPerDay : 0;
  let questionTotal = answeredSoFar + Math.max(0, todayTarget - answeredToday);
  const popAlreadyUnlocked = answeredSoFar >= POP_MINIMUM;
  const popUnlocksToday = !popAlreadyUnlocked && questionTotal >= POP_MINIMUM;
  const popTargetToday = questionTotal >= POP_TARGET;
  let popUnlockIndex = null;
  let popTargetIndex = null;
  const questionCurve = [];
  days.forEach((day, i) => {
    if (day.kind === 'study') {
      day.lessons = Math.min(lessonsFor(day.phase), Math.floor(questionsPerDay / LESSON_SIZE));
      day.practiceQuestions = questionsPerDay - day.lessons * LESSON_SIZE;
      questionTotal += questionsPerDay;
      if (!popAlreadyUnlocked && !popUnlocksToday && popUnlockIndex == null && questionTotal >= POP_MINIMUM) popUnlockIndex = i;
      if (!popTargetToday && popTargetIndex == null && questionTotal >= POP_TARGET) popTargetIndex = i;
    }
    questionCurve.push(questionTotal);
  });
  if (popUnlockIndex != null) mark(popUnlockIndex, 'Your Possibility of Passing appears', 'pop');
  checkpoints.sort((a, b) => a.date - b.date);

  const phaseCount = phase => studyIndexes.filter(i => days[i].phase === phase).length;

  // Today isn't in the plan's days; it follows the mix of the first study day
  const firstStudyDay = studyCount ? days[studyIndexes[0]] : null;
  const todayWd = weekdayIndex(start);
  const todayDay = studyDays[todayWd]
    ? { date: start, wd: todayWd, kind: 'study', phase: firstStudyDay?.phase ?? 'learn', lessons: firstStudyDay?.lessons ?? 0, practiceQuestions: firstStudyDay?.practiceQuestions ?? questionsPerDay }
    : { date: start, wd: todayWd, kind: 'off' };

  return {
    exam,
    days,
    questionsPerDay,
    cap,
    studyDays: studyCount,
    reserveDays: days.filter(day => day.kind === 'reserve').length,
    sims: checkpoints.filter(c => c.type === 'cat' || c.type === 'simulation').length,
    covered: Math.min(poolSize, questionsPerDay * studyCount),
    poolSize,
    checkpoints,
    phases: { learn: phaseCount('learn'), practice: phaseCount('practice'), simulate: phaseCount('simulate') },
    questionCurve,
    questionTotal,
    popUpdates: questionTotal >= POP_MINIMUM ? 1 + Math.floor((questionTotal - POP_MINIMUM) / POP_INTERVAL) : 0,
    popUnlockIndex,
    popUnlocksToday,
    popAlreadyUnlocked,
    popTargetIndex,
    firstStudyDay,
    today: todayDay,
    firstStudyDayNumber: studyCount ? studyIndexes[0] + 1 : null,
    studyDaysUntilPop: popUnlockIndex == null ? null : studyIndexes.filter(i => i <= popUnlockIndex).length,
  };
}

export function dayLabel(day) {
  if (day.kind === 'exam') return 'Exam day';
  if (day.kind === 'off') return 'Day off';
  if (day.kind === 'reserve') return 'Catch-up day';
  const phase = { learn: 'Lessons first', practice: 'Practice questions', simulate: 'Exam simulation' }[day.phase];
  const lessons = day.lessons ? `${day.lessons} lesson${day.lessons > 1 ? 's' : ''} + ` : '';
  return `${phase}, ${lessons}${day.practiceQuestions} practice questions`;
}

export function formatMinutes(minutes) {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!hours) return `${rest} min`;
  return rest ? `${hours} hr ${rest} min` : `${hours} hr`;
}
